import React, { useState, useEffect, useRef, memo } from 'react';
import { UserAvatarGroup } from '../../components/UserAvatar';
import PortfolioPermission from '../../components/PortfolioPermission';

const PortfolioCard = memo(({ portfolio, onView, onEdit, onDelete }) => {
	const [showDropdown, setShowDropdown] = useState(false);
	const dropdownRef = useRef(null);

	// Get real users from portfolio data, with fallback mock data for testing
	const portfolioUsers =
		portfolio.users && portfolio.users.length > 0 ? portfolio.users : [];

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setShowDropdown(false);
			}
		};

		if (showDropdown) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showDropdown]);

	return (
		<div className="portfolio-card">
			<div className="portfolio-card-header"></div>

			{/* Card Content */}
			<div className="portfolio-card-content">
				<div className="d-flex justify-content-between align-items-start">
					<div className="d-flex justify-content-between align-items-start flex-grow-1">
						<div className="d-flex flex-column">
							<h1 className="portfolio-card-title">{portfolio.name}</h1>
							{portfolio.company && <p>{portfolio.company}</p>}
							<h5 className="portfolio-card-description">
								{portfolio.propertyCount || 0} Properties
							</h5>
						</div>
						<div>
							<div className="portfolio-stats-icons">
								<div className="row">
									<div className="col-md-6">
										<div className="stat-item" title="Violations">
											<i className={`bi bi-exclamation-circle-fill`} />
											<span className="stat-count">
												{portfolio.openViolationsCount}
											</span>
										</div>
									</div>

									<div className="col-md-6">
										<div className="stat-item" title="Properties">
											<i className="bi bi-buildings" />
											<span className="stat-count">
												{portfolio.propertyCount}
											</span>
										</div>
									</div>

									<div className="col-md-6">
										<div className="stat-item" title="Certifications">
											<i className="bi bi-award" />
											<span className="stat-count">
												{portfolio.certificatesCount}
											</span>
										</div>
									</div>
									<div className="col-md-6">
										<div className="stat-item" title="Uploads">
											<i className="bi bi-cloud-upload" />
											<span className="stat-count">
												{portfolio.uploadsCount}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div>
						<div className="portfolio-menu-dropdown" ref={dropdownRef}>
							<button
								className="btn btn-link portfolio-menu-btn"
								onClick={() => setShowDropdown(!showDropdown)}
							>
								<i className="bi bi-three-dots-vertical" />
							</button>
							{showDropdown && (
								<div className="portfolio-dropdown-menu">
									<PortfolioPermission
										portfolio={portfolio}
										requiredPermission="admin"
									>
										<button
											className="dropdown-item"
											onClick={() => {
												onEdit(portfolio.id);
												setShowDropdown(false);
											}}
										>
											<i className="bi bi-info-circle me-2" />
											More Info
										</button>
									</PortfolioPermission>
									<PortfolioPermission
										portfolio={portfolio}
										requiredPermission="admin"
									>
										<button
											className="dropdown-item text-danger"
											onClick={() => {
												onDelete(portfolio.id);
												setShowDropdown(false);
											}}
										>
											<i className="bi bi-trash me-2" />
											Delete
										</button>
									</PortfolioPermission>
									<PortfolioPermission
										portfolio={portfolio}
										requiredPermission="admin"
										invert
									>
										<button
											className="dropdown-item"
											onClick={() => {
												onView(portfolio.id);
												setShowDropdown(false);
											}}
										>
											<i className="bi bi-eye me-2" />
											View Portfolio
										</button>
									</PortfolioPermission>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Card Footer */}
			<div className="portfolio-card-footer">
				{/* User Avatars - Bottom Left */}
				<div className="portfolio-avatars">
					<UserAvatarGroup
						referenceId={`grid-item-${portfolio.id}`}
						users={portfolioUsers}
						maxVisible={4}
						size="large"
						showTooltip={true}
						onUserClick={(user) => {
							// Handle user click if needed
						}}
						onMoreClick={() => {
							// Handle "more users" click if needed
						}}
					/>
				</div>

				{/* View Portfolio Button - Center Bottom */}
				<div className="portfolio-view-button-wrapper">
					<button
						className="btn btn-primary"
						onClick={() => onView(portfolio.id)}
					>
						View Portfolio
					</button>
				</div>
			</div>
		</div>
	);
});

const PortfolioGrid = memo(({ portfolios, onView, onEdit, onDelete }) => {
	return (
		<div className="portfolio-grid">
			{portfolios.map((portfolio) => (
				<PortfolioCard
					key={`grid-${portfolio.id}`}
					portfolio={portfolio}
					onView={onView}
					onEdit={onEdit}
					onDelete={onDelete}
				/>
			))}
		</div>
	);
});

export default PortfolioGrid;
